/* global modForm modUtils Modalytics */
import {
	addFallbackOwnHome,
	deleteFormDataCookie,
	getCurrentStep,
	getFormDataCookie,
	getFormsServiceApiDomain,
	initApiErrorFallbackExperience,
	initSentryScript,
	preprocessQSFormData,
	processFormsServiceApiSubmitSuccess,
	removeLoader,
	saveFormDataToCookie,
	showNextStep,
	swapRecaptchaTokenWithFlag,
	switchFormLoadingState,
	switchFormSubmitState,
	trackEcommerceWidgetEvents,
	trackFormServiceApiPayload,
	trackFormSubmissionFlow,
	updateFormDataFormatForFormsService,
	useInitialQueryParamsFromSessionStorage,
	getBrowserDetails,
	getReferrerDomain,
	trackTimeOnPage
} from '../../helpers';
import { isFormServiceAPIPayloadLengthValid } from '../../validators';
/**
 * Submit form data to HS Forms Service Lead Capture API endpoint
 * @param {Object} formData - form data
 * @param {Function} successCallback - success callback
 * @param {Function} errorCallback - error callback
 */
export function submitFormDataToFormsServiceApi(formData = {}, successCallback, errorCallback) {
	trackFormSubmissionFlow({ inSubmitFormDataToFormsServiceApi: true });

	const apiDomain = getFormsServiceApiDomain();
	let apiUrl = apiDomain + 'forms/inquiries';

	if (modForm.opts.useFormsServiceProxyApi) {
		apiUrl = '/inquiries';
	}

	trackFormSubmissionFlow({ formSubmitRequestURL: apiUrl });

	// Check if the formdata has UtilityProvider, if its present encode its value
	if (formData.UtilityProvider) {
		trackFormSubmissionFlow({ encodingUtilityProvider: true });
		formData = modUtils.encodeValueInObject(formData, 'UtilityProvider');
	}

	// Add fallback OwnHome value if needed
	formData = addFallbackOwnHome(formData);

	// Remove recaptcha token string and add flag
	formData = swapRecaptchaTokenWithFlag(formData);

	// If user encounters JS error and session refreshes, send initial query params & clean up form data
	formData = useInitialQueryParamsFromSessionStorage(formData);

	// Save updated formData in the cookie
	if (formData && Object.keys(formData)) {
		saveFormDataToCookie(formData);
	}

	// Format to new API formData structure
	let updatedFormData = {};
	updatedFormData = updateFormDataFormatForFormsService(formData);

	if (modForm.opts.formSubmittingCallbackBeforeFormPost && 'function' === typeof modForm.opts.formSubmittingCallbackBeforeFormPost) {
		updatedFormData = modForm.opts.formSubmittingCallbackBeforeFormPost(updatedFormData);
		trackFormSubmissionFlow({ formSubmittingCallbackBeforeFormPostCalled: true });
	}

	if (isFormServiceAPIPayloadLengthValid(updatedFormData)) {
		const optionalAdditionalNodes = [
			{ name: 'landingPageDomain', value: window.location.hostname },
			{ name: 'referrerDomain', value: getReferrerDomain() },
			{ name: 'browser', value: getBrowserDetails().compact },
			{ name: 'timeOnPage', value: trackTimeOnPage.complete() }
		];

		if (!updatedFormData.trackingAttributes) {
			updatedFormData.trackingAttributes = {};
		}

		if (!updatedFormData.trackingAttributes.additionalAttributes) {
			updatedFormData.trackingAttributes.additionalAttributes = {};
		}

		for (const node of optionalAdditionalNodes) { // eslint-disable-line guard-for-in
			const { name, value } = node;
			updatedFormData.trackingAttributes.additionalAttributes[name] = value;
			// Check the payload length limit is exceeding after adding each optional node. If exceeds remove that specific node and try to add next one.
			if (!isFormServiceAPIPayloadLengthValid(updatedFormData)) {
				delete updatedFormData.trackingAttributes.additionalAttributes[name];
			}
		}
	}

	modForm.opts.formData = updatedFormData;

	if (modForm.isSubmitting === false) {
		modForm.isSubmitting = true;
		let response = {};
		trackFormSubmissionFlow({ formSubmitRequestStarted: true });

		const request = new XMLHttpRequest();
		trackFormSubmissionFlow({ formSubmitRequestUnsent: true });

		request.open('POST', apiUrl);
		trackFormSubmissionFlow({ formSubmitRequestOpened: true });
		request.setRequestHeader('Content-Type', 'application/json');

		const isRequestBodyInvalid = !updatedFormData || !Object.keys(updatedFormData).length;
		const storedFormData = getFormDataCookie();
		if (storedFormData && isRequestBodyInvalid) {
			const parsedData = JSON.parse(storedFormData);
			if (Object.keys(parsedData).length) {
				Object.entries(parsedData).forEach(([key, value]) => {
					parsedData[key] = decodeURIComponent(value);
				});
			}
			const processedData = preprocessQSFormData(parsedData);
			updatedFormData = updateFormDataFormatForFormsService(processedData);
			trackFormSubmissionFlow({ usedFormDataFromCookie: true });
		}

		request.send(JSON.stringify(updatedFormData));
		trackFormServiceApiPayload(updatedFormData);
		deleteFormDataCookie();
		trackFormSubmissionFlow({ formSubmitRequestSent: true });

		request.addEventListener('progress', function() {
			trackFormSubmissionFlow({ formSubmitRequestLoading: true });
		});

		request.addEventListener('load', function() {
			const xhr = this;
			if (this.response) {
				response = { ...response, ...JSON.parse(this.response) };
				if (this.readyState === 4 && this.status === 200) {
					trackFormSubmissionFlow({ formSubmitRequestSuccessful: true });
					document.body.classList.add('prevent-abandonment');
					if ('function' === typeof successCallback) {
						successCallback(response);
					}

					if (response.commissionDetails) {
						if (response.commissionDetails.firePixel === true) {
							modForm.opts.isFirePixelTrue = true;
						}
					}

					// Needed for Frontdoor's Exclusive Match Experience
					if (response) {
						if (formData.PC) {
							response.zip = formData.PC;
						}
						if (formData.SP) {
							response.state = formData.SP;
						}

						if (formData.CT) {
							response.city = formData.CT;
						}
					}

					// Redshift Transforms that normally happened on the TYP/Submit Success
					if (response.dataCaptureKey) {
						const dataCaptureKey = response.dataCaptureKey;

						Modalytics.heap('addEventProperties', {
							/* eslint-disable camelcase */
							project_id: dataCaptureKey,
							form_path: modForm.opts.landingPage,
							DCK: dataCaptureKey
							/* eslint-enable camelcase */
						});
					}

					// E-Commerce Matching Logic (Exclusive = Redirect / Widget, Shared = HRC Secondary Button Addition)
					if (response.vendors && response.vendors.length > 0) {
						const exclusiveMatchKeys = Object.values(modForm.opts.eCommerceVendorKeys.exclusive),
							exclusiveMatch = response.vendors.find(match => exclusiveMatchKeys.flat(Infinity).includes(String(match.vendorKey))),
							exclusiveVendorKey = exclusiveMatch?.vendorKey,
							sharedMatchKeys = Object.values(modForm.opts.eCommerceVendorKeys.shared),
							isSharedMatch = response.vendors.filter(match => sharedMatchKeys.flat(Infinity).includes(String(match.vendorKey))),
							hashedDCK = response?.publicLeadIdentifer,
							isWholeSaleMatch = response.vendors.length === 1 && response.vendors[0].wholeSale;

						if (response.vendors.length > 1 && isSharedMatch.length > 0) {
							const matchingKeys = isSharedMatch.map(match => match.vendorKey).join(',');
							modForm.opts.sharedParam = `shared=${matchingKeys}`;
							modForm.opts.isSharedMatch = isSharedMatch;
						} else if (exclusiveVendorKey) {
							// load sentry script in production
							if (modUtils.getEnv() === 'production') {
								initSentryScript();
							}
							const sentryLogLevel = 'error';

							const matchedVendor = Object.keys(modForm.opts.eCommerceVendorKeys.exclusive).find(key => modForm.opts.eCommerceVendorKeys.exclusive[key].includes(exclusiveVendorKey));
							modForm.opts.exclusiveMatchVendor = matchedVendor;

							if (response.vendors.length === 1 && (exclusiveMatch.vendorLeadDeliveryToken || modForm.opts.eCommerceVendorKeys.exclusive.cove.includes(exclusiveVendorKey) || (exclusiveMatch.consentIntegrationData && exclusiveMatch.consentIntegrationData.redirectUrl))) {
								modForm.opts.isExclusiveMatch = true;

								if (exclusiveMatch.vendorLeadDeliveryToken && modForm.opts.eCommerceVendorKeys.exclusive.frontdoor.includes(exclusiveVendorKey)) {
									const frontdoorBasesUrl = 'https://shopping.ahs.com/plans/get-my-quote/select?leadData=',
										frontdoorUtmSource = '&utm_medium=affiliate&utm_source=modernize&utm_campaign=modernize_ecomm';

									modForm.opts.frontdoorLeadId = exclusiveMatch.vendorLeadDeliveryToken ? exclusiveMatch.vendorLeadDeliveryToken : '';

									if (response.city && response.state) {
										modForm.opts.userCity = response.city;
										modForm.opts.userState = response.state;
									} else if (modForm.opts.userLocation) {
										const userLocation = modForm.opts.userLocation.split(',');
										modForm.opts.userCity = userLocation[0].trim();
										modForm.opts.userState = userLocation[1].trim();
									}

									const piiData = `;;${modForm.opts.userCity};${modForm.opts.userState};${response.zip};${updatedFormData.address};;;;;;;${modForm.opts.frontdoorLeadId};`;
									const piiEncoded = btoa(piiData);

									modForm.opts.exclusiveMatchResultsPage = `${frontdoorBasesUrl}${piiEncoded}${frontdoorUtmSource}`;
								} else if (modForm.opts.eCommerceVendorKeys.exclusive.cove.includes(exclusiveVendorKey)) {
									modForm.opts.exclusiveMatchResultsPage = 'https://www.covetrack.com/28KL6/2CTPL';
								} else if (exclusiveMatch.consentIntegrationData.redirectUrl) {
									modForm.opts.exclusiveMatchResultsPage = exclusiveMatch.consentIntegrationData.redirectUrl;
								}

								Modalytics.heap('track', ['E-Commerce Flow', {
									companyName: exclusiveMatch.vendorDisplayName,
									destinationLink: modForm.opts.exclusiveMatchResultsPage,
									matchCriteria: 'Exclusive (Redirect)'
								}]);
							} else if (exclusiveMatch.consentIntegrationData && (exclusiveMatch.consentIntegrationData.widgetUrl || exclusiveMatch.consentIntegrationData.additionalData)) {
								if (!modForm.opts.showExclusiveWidgetInStep) {
									const insertHtmlAdjacent = (htmlContent, targetNode, position = 'after') => {
										// If we don't receive a valid `htmlContent` or `targetNode` string, do nothing.
										if (typeof htmlContent !== 'string' || !htmlContent.trim() || typeof targetNode !== 'string' || !targetNode.trim()) {
											return;
										}

										const node = document.querySelector(targetNode);
										if (!node) {
											return;
										}

										const pos = position === 'before' ? 'beforebegin' : 'afterend';
										node.insertAdjacentHTML(pos, htmlContent);
									};

									insertHtmlAdjacent(
										'<dialog class="modal modal--accessible modal--widget" tabindex="-1" role="dialog" aria-hidden="true"><div class="modal-dialog"><div class="modal-content"><div class="modal-header"><button class="modal-header--close" data-close="dialog" aria-label="Close"><svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 64 384 384" fill="currentColor" aria-hidden="true"><path d="M55.1 73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L147.2 256 9.9 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192.5 301.3 329.9 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.8 256 375.1 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192.5 210.7 55.1 73.4z"/></svg></button></div><div class="modal-body"></div></div></div></dialog>',
										modForm.opts.isLeadWidget ? '.footer-container' : '#footer-component',
										'before'
									);
								}

								modForm.opts.isExclusiveWidgetMatch = true;
								let responseToken = '';

								const widgetModal = document.querySelector('.modal--widget'),
									widgetModalBody = widgetModal ? widgetModal.querySelector('.modal-body') : null,
									widgetStep = document.createElement('li'),
									stepInner = document.createElement('div'),
									secureText = modForm.opts.form.querySelectorAll('.secure-text');

								if (modForm.opts.showExclusiveWidgetInStep) {
									widgetStep.className = 'swiper-slide swiper-no-swiping step step--show-widget';
									widgetStep.setAttribute('aria-hidden', 'true');
									widgetStep.setAttribute('tabindex', '-1');
									widgetStep.setAttribute('data-bind', 'step');
									widgetStep.setAttribute('data-step-name', 'widget');

									stepInner.className = 'step-inner';
									widgetStep.appendChild(stepInner);

									modForm.opts.form.querySelector('.swiper-wrapper').appendChild(widgetStep);
									widgetStep.setAttribute('id', `step${modForm.opts.form.querySelectorAll(modForm.opts.stepsSelector).length + 1}`);
									if (modForm.opts.formSwiperInstance) {
										modForm.opts.formSwiperInstance.update();
									}
								} else {
									stepInner.className = 'modal-inner';
									widgetModalBody.appendChild(stepInner);
								}

								const loaderLink = document.querySelector('.loader__link');
								if (loaderLink) {
									loaderLink.style.display = 'none';
								}

								if (exclusiveMatch.consentIntegrationData.additionalData && modForm.opts.eCommerceVendorKeys.exclusive.airo.includes(exclusiveVendorKey)) {
									responseToken = JSON.stringify(exclusiveMatch.consentIntegrationData.additionalData);

									if (exclusiveMatch.consentIntegrationData.additionalData.trade && exclusiveMatch.consentIntegrationData.additionalData.campaignId) {
										const targetContainer = document.createElement('div'),
											initializeScript = document.createElement('script');

										targetContainer.id = 'airo-anchor';
										stepInner.appendChild(targetContainer);
										stepInner.style.overflow = 'auto';

										window.airoBookingForm = {
											zipCode: updatedFormData.zip,
											trade: exclusiveMatch.consentIntegrationData.additionalData.trade,
											firstName: updatedFormData.firstName,
											lastName: updatedFormData.lastName,
											address: updatedFormData.address,
											email: updatedFormData.email,
											phone: updatedFormData.phone,
											campaignId: exclusiveMatch.consentIntegrationData.additionalData.campaignId,
											apptScheduledCallback: function() {
												Modalytics.heap('track', ['E-Commerce Flow | Summary Shown']);
												trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
													name: 'E-Commerce Flow | Summary Shown'
												});
											},
											doneCallback: function() {
												Modalytics.heap('track', ['E-Commerce Flow | Completed']);
												trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
													name: 'E-Commerce Flow | Completed'
												}, function() {
													history.pushState(null, null, `${modForm.opts.fullPageUrl}&refreshExp=true`);
													window.location = modForm.opts.resultsPage;
												});
											}
										};

										initializeScript.src = `https://form.contractorappointments.com/js/embed-form.js?v=${new Date().getTime()}`;
										document.body.appendChild(initializeScript);
									}
								} else {
									responseToken = exclusiveMatch.consentIntegrationData.widgetUrl;

									if (!modForm.opts.eCommerceVendorKeys.exclusive.angi.includes(exclusiveVendorKey)) {
										window.Sentry?.captureMessage('E-Commerce Widget | Not matched with Angi', sentryLogLevel);
									}

									if (modForm.opts.eCommerceVendorKeys.exclusive.angi.includes(exclusiveVendorKey)) {
										const dropinContainer = document.createElement('div'),
											initializeScript = document.createElement('script'),
											scriptUrl = 'https://request.angi.com/hcc/dropin-112a615a05e36545.js';

										dropinContainer.className = 'dropin-container';
										stepInner.appendChild(dropinContainer);

										fetch(scriptUrl)
											.then(angiResp => {
												if (!angiResp.ok) {
													window.Sentry?.captureMessage(`E-Commerce Widget | Script Response Failed ${scriptUrl} | ${responseToken}`, sentryLogLevel);
													Modalytics.heap('track', ['E-Commerce Widget | Script Error', {
														error: `Response Error: ${angiResp.status} ${angiResp.statusText}`
													}]);
													trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
														name: 'E-Commerce Widget | Script Error'
													});
												}

												initializeScript.src = scriptUrl;
												initializeScript.onload = function() {
													if (window.initializeDropIn) {
														window.initializeDropIn(responseToken);
														Modalytics.heap('track', ['E-Commerce Widget | Initialize Drop In Successful']);
														trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
															name: 'E-Commerce Widget | Initialize Drop In Successful'
														});
													} else {
														window.Sentry?.captureMessage(`E-Commerce Widget | Undefined initializeDropIn | ${responseToken}`, sentryLogLevel);
													}
												};
												document.body.appendChild(initializeScript);
											})
											.catch(angiErr => {
												window.Sentry?.captureMessage(`E-Commerce Widget | Script Error ${scriptUrl} | ${responseToken}`, sentryLogLevel);
												Modalytics.heap('track', ['E-Commerce Widget | Script Error', {
													error: `Fetch Error: ${angiErr}`
												}]);
												trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
													name: 'E-Commerce Widget | Script Error'
												});
											});
									} else {
										const iframe = document.createElement('iframe');

										iframe.src = responseToken;
										stepInner.appendChild(iframe);
									}
								}

								modForm.opts.autoRedirectToTyOrSorryPage = false;
								switchFormLoadingState(false);
								switchFormSubmitState(false);

								if (modForm.opts.showExclusiveWidgetInStep) {
									modForm.opts.form.classList.add('widget-visible');
									showNextStep();
									widgetStep.setAttribute('aria-hidden', 'false');

									const stepElementsToHide = [
										modForm.opts.backButtons,
										modForm.opts.progressBar,
										modForm.opts.modQuoteButtonWrapper,
										secureText
									];

									stepElementsToHide.forEach(function(element) {
										if (element) {
											element.forEach(function(el) {
												el.style.display = 'none';
											});
										}
									});
								} else {
									const $activeStep = getCurrentStep(),
										stepElementsToDisable = [
											$activeStep.querySelectorAll('button'),
											modForm.opts.backButtons
										];

									stepElementsToDisable.forEach(function(element) {
										if (element) {
											element.forEach(function(el) {
												el.setAttribute('disabled', 'disabled');
											});
										}
									});

									const closeModalButton = widgetModal.querySelector('[data-close="dialog"]');

									widgetModal.show();
									widgetModal.setAttribute('aria-hidden', 'false');
									widgetModal.focus();
									document.body.classList.add('no-scroll');

									function closeWidgetModal() { // eslint-disable-line
										widgetModal.close();
										widgetModal.setAttribute('aria-hidden', 'true');
										document.body.classList.remove('no-scroll');
										history.pushState(null, null, `${modForm.opts.fullPageUrl}&refreshExp=true`);
										window.location = modForm.opts.resultsPage;
									}

									if (closeModalButton) {
										closeModalButton.addEventListener('click', function() {
											closeWidgetModal();
										});
									}

									document.addEventListener('keydown', function(e) {
										if (e.key === 'Escape') {
											closeWidgetModal();
										}
									});
								}

								Modalytics.heap('track', ['E-Commerce Widget', {
									companyName: exclusiveMatch.vendorDisplayName,
									pingToken: responseToken,
									matchCriteria: 'Exclusive (Widget)'
								}]);

								trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
									name: 'E-Commerce Widget'
								});

								// Listen for when the widget has been completed (contains both listeners from Angi / Networx)
								window.addEventListener('message', function(event) {
									const data = event.data;

									if (!data) {
										window.Sentry?.captureMessage(`E-Commerce Widget | Undefined event.data | ${exclusiveMatch.vendorDisplayName} | ${responseToken}`, sentryLogLevel);
									}

									if (modForm.opts.eCommerceVendorKeys.exclusive.angi.includes(exclusiveVendorKey) || modForm.opts.eCommerceVendorKeys.exclusive.networx.includes(exclusiveVendorKey)) {
										if (data && (data.event === 'pageState' && data.payload === 'WidgetLoaded') || (data.source === 'networx' && data.payload && data.payload.pageLoad)) {
											Modalytics.heap('track', ['E-Commerce Widget | Loaded', {
												companyName: exclusiveMatch.vendorDisplayName,
												message: JSON.stringify(data)
											}]);
											trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
												name: 'E-Commerce Widget | Loaded'
											});
										}
									}

									if (data && (data.event === 'pageState' && data.payload === 'ThankYou') || (data.source === 'networx' && data.payload && data.payload.leadDone)) {
										const loadedWidgetCompanyName = Object.keys(modForm.opts.eCommerceVendorKeys.exclusive).find(key => modForm.opts.eCommerceVendorKeys.exclusive[key].includes(exclusiveVendorKey))?.toLowerCase();
										if (loadedWidgetCompanyName) {
											modForm.opts.resultsPage += (modForm.opts.resultsPage.indexOf('?') < 0 ? '?' : '&') + `widget=${loadedWidgetCompanyName}`;
											if (isWholeSaleMatch) {
												modForm.opts.resultsPage += '&wholesale=true';
											}
										}
										Modalytics.heap('track', ['E-Commerce Widget | Completed', {
											resultsPage: modForm.opts.resultsPage
										}]);
										trackEcommerceWidgetEvents(hashedDCK, exclusiveVendorKey, {
											name: 'E-Commerce Widget | Completed'
										}, function() {
											history.pushState(null, null, `${modForm.opts.fullPageUrl}&refreshExp=true`);
											window.location = modForm.opts.resultsPage;
										});
									}
								});
							} else if (modForm.opts.eCommerceVendorKeys.exclusive.frontdoor.includes(exclusiveVendorKey) && !exclusiveMatch.vendorLeadDeliveryToken) {
								// If the vendorLeadDeliveryToken is not in the response (but they are an exclusive match), we will send the shared param to the HRC for Frontdoor regardless
								Modalytics.heap('track', ['E-Commerce Flow | Missing Token']);
								modForm.opts.sharedParam = `shared=${exclusiveVendorKey}`;
								modForm.opts.isSharedMatch = true;
							}
						}
					}

					// Change form submit state
					if (modForm.opts.showLoadingOverlay && !modForm.opts.isExclusiveWidgetMatch) {
						switchFormSubmitState('submitted');
						setTimeout(function() {
							processFormsServiceApiSubmitSuccess(response, xhr);
						}, 1000);
					} else {
						processFormsServiceApiSubmitSuccess(response, xhr);
					}
				} else {
					if (typeof modForm.opts.onGetMatchesBlocked === 'function' && this.status === 0) {
						// Status code 0 most common cause: IP Blocked
						modForm.opts.onGetMatchesBlocked();
					}
					// If there was an error, we first look for any error callback first
					if (typeof errorCallback === 'function') {
						errorCallback(xhr, this.status, response);
					}
				}
			}
		});

		request.addEventListener('loadend', function() {
			trackFormSubmissionFlow({ formSubmitRequestCompleted: true });
			modForm.isSubmitting = false;
			switchFormLoadingState(false);
		});

		request.addEventListener('error', function() {
			trackFormSubmissionFlow({ formSubmitRequestError: true });
			console.error('Something went wrong!');
			removeLoader();
			// Show fallback experience if API encounters network issues
			initApiErrorFallbackExperience();
			Modalytics.heap('track', ['API Error | Showing Fallback Experience', {
				function: 'submitFormDataToFormsServiceApi',
				status: `${this.status}|${this.responseText}`
			}]);
		});

		request.addEventListener('abort', function() {
			trackFormSubmissionFlow({ formSubmitRequestAborted: true });
		});

		request.addEventListener('timeout', function() {
			trackFormSubmissionFlow({ formSubmitRequestTimeOut: true });
		});
	}
}
